import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { ClassSchedule, DayOfWeek } from 'app/javascript/types';
import { DISPLAY_DAY_ORDER, DAY_LABELS, getCurrentDayOfWeek, trimEmptyEdges, ScheduleHighlight } from 'app/javascript/utils/scheduleHelpers';

const DEFAULT_HIGHLIGHT: ScheduleHighlight = { activeIds: [], nextUpcomingId: null };

interface ScheduleGridProps {
  schedules: ClassSchedule[];
  highlight?: ScheduleHighlight;
}

export default function ScheduleGrid({
  schedules,
  highlight = DEFAULT_HIGHLIGHT,
}: ScheduleGridProps) {
  const currentDay = getCurrentDayOfWeek();
  const visibleDays = trimEmptyEdges(DISPLAY_DAY_ORDER, schedules);

  const [expandedDays, setExpandedDays] = useState<Set<DayOfWeek>>(() => {
    const initial = new Set<DayOfWeek>();
    if (visibleDays.includes(currentDay)) {
      initial.add(currentDay);
    }
    return initial;
  });

  const toggle = (day: DayOfWeek) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(day)) {
        next.delete(day);
      } else {
        next.add(day);
      }
      return next;
    });
  };

  return (
    <div className="space-y-2">
      {visibleDays.map((day) => {
        const daySchedules = schedules.filter((s) => s.day_of_week === day);
        if (daySchedules.length === 0) return null;

        const isToday = day === currentDay;
        const isExpanded = expandedDays.has(day);

        return (
          <div
            key={day}
            className={`rounded-xl border overflow-hidden ${
              isToday ? 'border-yellow-400/30' : 'border-slate-700/30'
            }`}
          >
            {/* Day header — clickable */}
            <button
              onClick={() => toggle(day)}
              className={`w-full px-4 py-2.5 flex items-center gap-2 cursor-pointer transition-colors ${
                isToday
                  ? 'bg-yellow-400/10 text-yellow-400 hover:bg-yellow-400/15'
                  : 'bg-slate-800/60 text-slate-400 hover:bg-slate-800/80'
              }`}
            >
              <span className="text-sm font-bold tracking-wide">{DAY_LABELS[day]}</span>
              {isToday && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-yellow-400/20 text-yellow-400">
                  Hoje
                </span>
              )}
              {!isExpanded && (
                <span className="text-xs text-slate-500 ml-1">
                  {daySchedules.length} {daySchedules.length === 1 ? 'aula' : 'aulas'}
                </span>
              )}
              <ChevronDown
                className={`w-4 h-4 ml-auto flex-shrink-0 transition-transform duration-200 ${
                  isExpanded ? 'rotate-180' : ''
                } ${isToday ? 'text-yellow-400/60' : 'text-slate-500'}`}
              />
            </button>

            {/* Classes — collapsible */}
            {isExpanded && (
              <div className="divide-y divide-slate-700/30 bg-slate-900/30">
                {daySchedules.map((schedule) => {
                  const isActive = highlight.activeIds.includes(schedule.id);
                  const isNextUpcoming = schedule.id === highlight.nextUpcomingId;

                  return (
                    <div
                      key={schedule.id}
                      className={`flex items-center gap-4 px-4 py-3 ${
                        isActive
                          ? 'bg-yellow-400/5'
                          : isNextUpcoming
                            ? 'bg-slate-800/30'
                            : ''
                      }`}
                    >
                      <span className="text-sm font-mono text-yellow-400/80 w-[110px] flex-shrink-0">
                        {schedule.start_time} – {schedule.end_time}
                      </span>
                      <span className="text-sm text-white font-medium">{schedule.name}</span>
                      {isActive && (
                        <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-yellow-400/20 text-yellow-400 flex-shrink-0">
                          Agora
                        </span>
                      )}
                      {isNextUpcoming && (
                        <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-600/40 text-slate-300 flex-shrink-0">
                          Próxima
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
